import styled from '@emotion/styled'

import { ActionMenu } from '../../../components'
import { SmallTertiaryButton } from '../../../components/Button'
import { ROW_METRICS, rowBorder, rowNameButton } from '../../../components/DataTable'
import { CHART_WIDTH_SINGLE } from '../../../components/RetirementIncomeDonut'
import {
  DashboardAddButton,
  DashboardPageContainer,
  gutter,
} from '../../dashboards/dashboardStyles'
import { TABLET } from '../../../guided-toolbox/utils/breakpoints'

const DONUT_WIDTH = `${CHART_WIDTH_SINGLE}px`

// Wide enough to hold the at-retirement label and its help icon on one line.
const VALUES_BLOCK_WIDTH = '385px'

const MIN_CHART_GAP = '40px'

// Matches the padding the SVG puts above the arc.
const CHART_TOP_PADDING = '10px'

// Apply the content gutter at the tablet breakpoint, where this page switches to its narrow layout.
const contentGutter = gutter(TABLET)

const PageContainer = styled(DashboardPageContainer)`
  gap: 20px;

  /* Matches the space the page leaves below the last row. */
  padding-top: 12px;

  /* Tablet uses the mobile layout: cap the column near mobile width so it does not sprawl. */
  ${TABLET} {
    max-width: 560px;
    padding: 12px 0 30px;
    gap: 20px;
  }
`

const PageTitle = styled.h1`
  ${contentGutter};
  margin: 0;
  color: ${p => p.theme.mediumGray};
  font-size: 40px;
  font-weight: 300;
  line-height: 58px;

  ${TABLET} {
    color: ${p => p.theme.lightestGray};
    font-size: 23px;
    font-weight: 400;
    line-height: 24px;
    text-align: center;
  }
`

const PageTitleEmphasis = styled.strong`
  font-weight: 600;

  ${TABLET} {
    /* The narrow design breaks the line here rather than letting it wrap. */
    display: block;
    font-weight: 700;
  }
`

const CouldYouLive = styled.div`
  color: ${p => p.theme.darkestGray};
  font-size: 18px;
  font-weight: 400;
  line-height: 24px;

  ${TABLET} {
    display: none;
  }
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

// --- Your income after retiring ---

const IncomeBody = styled.div`
  ${contentGutter};
  display: grid;

  /* Split leftover space 1:2 between the gap and trailing margin. This keeps
     the values near the designed position without fixing them there. */
  grid-template-columns:
    ${DONUT_WIDTH} minmax(${MIN_CHART_GAP}, 1fr)
    minmax(min-content, ${VALUES_BLOCK_WIDTH}) minmax(0, 2fr);
  grid-template-areas: 'lockup . values .';
  align-items: start;

  ${TABLET} {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'chart'
      'values'
      'button';
    row-gap: 6px;
  }
`

// One grid area for the gauge and its button, so neither shifts the values column.
const IncomeLockup = styled.div`
  grid-area: lockup;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

const ChartCell = styled.div`
  grid-area: chart;
  width: 100%;
  max-width: ${DONUT_WIDTH};

  ${TABLET} {
    justify-self: center;
  }
`

const ValuesCell = styled.div`
  grid-area: values;
  margin-top: ${CHART_TOP_PADDING};
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${TABLET} {
    /* Fill the column so the centered rows stay put as they are revealed. */
    justify-self: stretch;
    align-items: center;
    margin-top: 0;
    gap: 16px;
  }
`

const ChangeIncomeButton = styled(SmallTertiaryButton)`
  grid-area: button;
  min-width: 160px;
  line-height: 22px;

  ${TABLET} {
    margin-top: 14px;
    width: 100%;
  }
`

// --- Other goals ---

// The top margin adds to the page gap for the designed 50px between sections.
const OtherGoalsHeader = styled.div`
  ${contentGutter};
  margin-top: 30px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas: 'copy button';

  align-items: center;
  column-gap: 20px;

  ${TABLET} {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'copy'
      'button';
    margin-top: 30px;
    row-gap: 30px;
  }
`

// Tall enough to hold the help icon clear of the previous line's descenders.
const INTRO_LINE_HEIGHT = 1.4

const IntroCopy = styled.div`
  grid-area: copy;
  margin: 0;
  color: ${p => p.theme.lightestGray};
  font-size: 23px;
  font-weight: 400;
  line-height: ${INTRO_LINE_HEIGHT};

  ${TABLET} {
    text-align: center;
  }
`

const IntroLead = styled.strong`
  font-weight: 700;
`

// Keep the help icon with the final line of copy.
const IntroHelp = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  vertical-align: middle;
`

const AddGoalButton = styled(DashboardAddButton)`
  grid-area: button;
  min-width: 150px;

  ${TABLET} {
    width: 100%;
  }
`

const GoalTable = styled.div`
  ${contentGutter};

  /* Adds to the section gap for the designed 34px between the copy and the
     column headings, less the header row's 2px transparent border. */
  margin-top: 12px;

  ${TABLET} {
    margin-top: 0;
  }
`

// Inherit the row's active or past color.
const GoalNameButton = styled.button`
  ${rowNameButton};
  display: block;
  max-width: 100%;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  ${TABLET} {
    font-size: 13px;
    line-height: 18px;
  }
`

const GoalMenu = styled(ActionMenu)`
  ${TABLET} {
    /* Expand only the trigger, not the menu item buttons. */
    & button[aria-expanded] {
      position: relative;

      /* Grow the 30px trigger to a 46px tap target without affecting layout. */
      &::after {
        content: '';
        position: absolute;
        inset: -8px;
      }
    }
  }
`

const EmptyGoals = styled.div`
  ${rowBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${ROW_METRICS.minHeight};
  padding: 0 ${ROW_METRICS.inlinePadding};
  border-radius: ${ROW_METRICS.radius};
  color: ${p => p.theme.mediumGray};
  font-size: 16px;
  line-height: 22px;

  /* Center wrapped lines as well as the overall message block. */
  text-align: center;

  ${TABLET} {
    /* Tighten the padding so the empty-state message fits on narrow screens. */
    padding: 18px 20px;
  }
`

const ErrorMessage = styled.div`
  ${contentGutter};
  color: #e31e27;
  font-size: 16px;
  line-height: 24px;
`

export {
  PageContainer,
  PageTitle,
  PageTitleEmphasis,
  Section,
  IncomeBody,
  IncomeLockup,
  ChartCell,
  ValuesCell,
  CouldYouLive,
  ChangeIncomeButton,
  OtherGoalsHeader,
  IntroCopy,
  IntroLead,
  IntroHelp,
  AddGoalButton,
  GoalTable,
  GoalNameButton,
  GoalMenu,
  EmptyGoals,
  ErrorMessage,
}
